export enum FeatureFlagKey {
  AbbyAidOnboardingAccountInformation = 'abbyaid_onboarding_account_information',
  DebugLogs = 'debug-logs',
  Flows = 'flows',
  EnableEditingOrders = 'enable-editing-orders',
  IsAutoFaxEnabled = 'auto_fax',
  EnableAdminOnboardingInternalPage = 'admin-onboarding-internal-page',
  FollowUpAssessments = 'follow-up-asseessments',
  TasksV2 = 'tasks-v-2',
  FuaRelease2 = 'fua-release-2',

  PatSunset = 'pat-sunset',
  Assign485ReviewOwner = 'qa-finalize-and-assign-task',
  ProfileDeduplication = 'profile-deduplication',
  AbbyIndex = 'abby-index',
  AssessmentRearchitecture = 'assessment-rearchitecture',
  AiAutofill = 'ai-autofill',
  AuthorizationUpdates = 'authorization-updates',
  EditGhostAuthStatus = 'edit-ghost-auth-status',
  VisitQaSunsetEnabled = 'visit-qa-sunset-enabled',
  ClinicalExports = 'clinical-exports',
  VisitScheduleRemediation = 'visit-schedule-remediation',
  SessionExpiredModal = 'session-expired-modal',
  DocumentRequests = 'document-requests',
  PatientsCaseManagerFilter = 'patients-case-manager-filter',
  ProcedureCodeRemediation = 'procedure-code-remediation',
  PatAssessmentRearchitecture = 'pat-assessment-rearchitecture',
  VisitChartedTimeRemediation = 'visit-charted-time-remediation',
  ServiceplanQaOptional = 'serviceplan-qa-optional',
  PatientNameQueriesLowImpact = 'patient-name-queries-low-impact',
  PatientDataQueries = 'patient-data-queries',
  PatientQueriesApiLowImpact = 'patient-queries-api-low-impact',
  PatientChartEvvExempt = 'patient-chart-evv-exempt',
  DocCollection = 'doc-collection',
  OnboardingDashboard = 'onboarding-dashboard',
  PatientChartCopilotV1 = 'patient-chart-copilot-v1',
  PayorAiBetaFeatures = 'payor-ai-beta-features',
  VisitAuthLinkage = 'visit-auth-linkage',
  ExternalValidation = 'external-validation',
  PhysicianListRestApi = 'physician-list-rest-api',
  DischargeResourceGenerationCelery = 'discharge-resource-generation-celery',
  CareDeliveryTransfer = 'care-delivery-transfer',
  TrackBuilder = 'track-builder',
  TrackBuilderBulkApply = 'track-builder-bulk-apply',
  PandaDocTemplateConfig = 'pandadoc-template-config',
  UseTemplateDrivenGates = 'use-template-driven-gates',
  AllowCreationResourcesForAbbyHub = 'allow-creation-resources-for-abby-hub',
  AdminFoundationalData = 'admin-foundational-data',
  SupportMultipleAssignees = 'support-multiple-assignees',
  DocumentVersioning = 'document-versioning',
  TrackBuilderTriggers = 'track-builder-triggers',
  AdminProcesses = 'admin-processes',
  DocumentRequestReRequest = 'document-request-re-request',
  // TODO(ENG-8074): remove after pdf-drag-drop pilot is complete and permanently on.
  PdfDragDrop = 'pdf-drag-drop',
  // TODO(ENG-8191): remove after pdf-edit-and-transfer release gate and permanently on.
  PdfEditAndTransfer = 'pdf-edit-and-transfer',
  DocCollectionSlackNotif = 'doc-collection-slack-notif',
  AdultPopulation = 'adult-population',
  WorkQueueOwner = 'work-queue-owner',
  WorkQueueStatus = 'work-queue-status',
  WorkQueueActivity = 'work-queue-activity',
  WorkQueueFlags = 'work-queue-flags',
  WorkQueueFlagsFilter = 'work-queue-flags-filter',
  WorkQueueFollowup = 'work-queue-followup',
  FaxAgentMultiLabel = 'fax-agent-multi-label',
  ParReadiness = 'par-readiness',
  // Same flag key as apps/caregiver-app's FeatureFlagKey.MedplumPasswordLoginFeatureRelease --
  // gates email/password login for AbbyAid. Once caregivers can log in with a self-chosen
  // password (instead of only Google Sign-In), a Gmail address is no longer needed to
  // provision their AbbyAid account.
  MedplumPasswordLoginFeatureRelease = 'medplum-password-login-feature-release',
  ManualReminders = 'manual-reminders',
  ExistingMedplumUserSorLink = 'existing-medplum-user-sor-link',
  AutoResourceGeneration = 'auto-resource-generation',
  SupportExistingCg = 'support-existing-cg',
  RemoveDocsMedplumFe = 'remove-docs-medplum-fe'
}

export type FeatureFlagName = keyof FeatureFlags

export interface FeatureFlags {
  abbyaidOnboardingAccountInformation: boolean
  debugLogs: boolean
  flows: boolean
  enableEditingOrders: boolean
  isAutoFaxEnabled: boolean
  enableAdminOnboardingInternalPage: boolean
  followUpAssessments: boolean
  tasksV2: boolean
  fuaRelease2: boolean

  patSunset: boolean
  assign485ReviewOwner: boolean
  profileDeduplication: boolean
  abbyIndex: boolean
  assessmentRearchitecture: boolean
  aiAutofill: boolean
  visitQaSunsetEnabled: boolean
  authorizationUpdates: boolean
  editGhostAuthStatus: boolean
  clinicalExports: boolean
  visitScheduleRemediation: boolean
  sessionExpiredModal: boolean
  documentRequests: boolean
  procedureCodeRemediation: boolean
  allowCaseManagerFilter: boolean
  visitChartedTimeRemediation: boolean
  serviceplanQaOptional: boolean
  patientNameQueriesLowImpact: boolean
  patAssessmentRearchitecture: boolean
  patientDataQueries: boolean
  patientQueriesApiLowImpact: boolean
  patientChartEvvExempt: boolean
  docCollection: boolean
  onboardingDashboard: boolean
  patientChartCopilotV1: boolean
  payorAiBetaFeatures: boolean
  visitAuthLinkage: boolean
  externalValidation: boolean
  physicianListRestApi: boolean
  dischargeResourceGenerationCelery?: boolean
  careDeliveryTransfer: boolean
  trackBuilder: boolean
  trackBuilderBulkApply: boolean
  pandadocTemplateConfig: boolean
  useTemplateDrivenGates: boolean
  allowCreationResourcesForAbbyHub: boolean
  // LD-backed: `undefined` until the client finishes bootstrapping.
  adminFoundationalData: boolean | undefined
  supportMultipleAssignees: boolean
  documentVersioning: boolean
  trackBuilderTriggers: boolean
  adminProcesses: boolean | undefined
  documentRequestReRequest: boolean
  pdfDragDrop: boolean
  pdfEditAndTransfer: boolean
  docCollectionSlackNotif: boolean
  adultPopulation: boolean
  workQueueOwner: boolean
  workQueueStatus: boolean
  workQueueActivity: boolean
  workQueueFlags: boolean
  workQueueFlagsFilter: boolean
  workQueueFollowup: boolean
  faxAgentMultiLabel: boolean
  parReadiness: boolean
  medplumPasswordLoginFeatureRelease: boolean
  manualReminders: boolean
  autoResourceGeneration: boolean
  supportExistingCg: boolean
  removeDocsMedplumFe: boolean
  existingMedplumUserSorLink: boolean
}
